import * as React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Button,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import EventIcon from "@mui/icons-material/Event";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import AdminPageHeader from "../components/AdminPageHeader";
import { useApi } from "../hooks/useApi";
import { useQuery } from "@tanstack/react-query";

function getMemberName(member) {
  return (
    member.name ||
    [member.first_name, member.last_name].filter(Boolean).join(" ") ||
    member.username ||
    "—"
  );
}

function formatDate(dateString) {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateString;
  }
}

function formatDateTime(dateString) {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
}

function formatRsvpStatus(status) {
  if (!status) return "N/A";
  const s = String(status).toLowerCase();
  if (s === "going" || s === "yes") return "Going";
  if (s === "maybe") return "Maybe";
  if (s === "not_going" || s === "no") return "Not going";
  return status;
}

export default function CircleUserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const [member, setMember] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const fetchMember = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await api.get(`/admin/circle/members/${id}`);
        setMember(data);
      } catch (err) {
        console.error("Error fetching member:", err);
        setError(err.message || "Failed to fetch member");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchMember();
    }
  }, [id, api]);

  const { data: eventsData, isLoading: eventsLoading } = useQuery({
    queryKey: ["circle-member-events", id],
    queryFn: () => api.get(`/admin/circle/members/${id}/events`),
    enabled: !!id && !!member,
  });

  const events = eventsData?.events ?? [];
  const memberName = member ? getMemberName(member) : "—";

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Home
                </Typography>
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard/circle"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Circle
                </Typography>
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard/circle/users"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Users
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  {memberName}
                </Typography>
              </Breadcrumbs>
            </Box>

            <AdminPageHeader
              title="Member Details"
              description="View Circle community member information"
              icon={<PeopleIcon sx={{ color: "primary.main" }} />}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : member ? (
              <>
                <Card variant="outlined">
                  <CardContent sx={{ pt: 2, pb: 2 }}>
                    <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mb: 1.5 }}>
                      Member Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                          Name
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                          {getMemberName(member)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                          Email
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                          {member.email ?? "—"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                          Member ID
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }} fontFamily="monospace">
                          {member.id ?? "—"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                          Username
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                          {member.username ?? "—"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                          First Name
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                          {member.first_name ?? "—"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                          Last Name
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                          {member.last_name ?? "—"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                <Card variant="outlined" sx={{ mt: 3 }}>
                  <CardContent sx={{ pt: 2, pb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                      <EventIcon color="primary" />
                      <Typography variant="h6" fontWeight="bold">
                        Events Attending
                      </Typography>
                    </Box>
                    {eventsLoading ? (
                      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={32} />
                      </Box>
                    ) : (
                      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Event</TableCell>
                                <TableCell>Event ID</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>RSVP Date</TableCell>
                                <TableCell>Event Date</TableCell>
                                <TableCell align="right"></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {events.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">
                                      No events registered
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ) : (
                                events.map((rsvp) => (
                                  <TableRow key={rsvp.id} hover>
                                    <TableCell>
                                      <Typography fontWeight={500}>
                                        {rsvp.event_name ?? `Event ${rsvp.event_id}`}
                                      </Typography>
                                    </TableCell>
                                    <TableCell fontFamily="monospace">
                                      {rsvp.event_id ?? "—"}
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={formatRsvpStatus(rsvp.rsvp_status ?? rsvp.status)}
                                        size="small"
                                        variant="outlined"
                                        color={
                                          rsvp.rsvp_status === "yes" || rsvp.status === "going"
                                            ? "success"
                                            : rsvp.rsvp_status === "maybe" || rsvp.status === "maybe"
                                              ? "warning"
                                              : "default"
                                        }
                                      />
                                    </TableCell>
                                    <TableCell>
                                      {formatDateTime(rsvp.rsvp_date ?? rsvp.created_at)}
                                    </TableCell>
                                    <TableCell>
                                      {formatDate(rsvp.event_starts_at ?? rsvp.starts_at)}
                                    </TableCell>
                                    <TableCell align="right">
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<VisibilityIcon />}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          rsvp.event_id &&
                                            navigate(`/dashboard/circle/events/${rsvp.event_id}`);
                                        }}
                                        disabled={!rsvp.event_id}
                                      >
                                        View
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : (
              <Alert severity="warning">Member not found</Alert>
            )}
          </Container>
          <Footer />
        </Box>
      </Box>
    </>
  );
}
